import { findMardColor, MARD_CATALOG_METADATA, type MardCatalogEntry } from "../catalog/mardCatalog";
import { cloneProject, occupiedCount, recalculatePalette } from "./pattern";
import type { PaletteEntry, RecognitionIssue, RecognitionResult } from "./types";

export type MardSelectionOutcome =
  | { status: "requires-merge"; duplicate: PaletteEntry }
  | { status: "applied"; recognition: RecognitionResult; merged: boolean };

function updateIssues(issues: RecognitionIssue[], issueId: string | undefined, oldCode: string, nextCode: string): RecognitionIssue[] {
  return issues.map((issue) => ({
    ...issue,
    resolved: issueId != null && issue.id === issueId ? true : issue.resolved,
    candidates: issueId != null && issue.id === issueId
      ? [nextCode]
      : issue.candidates.map((candidate) => candidate === oldCode ? nextCode : candidate),
  }));
}

function withRecalculatedTotals(recognition: RecognitionResult, issues: RecognitionIssue[]): RecognitionResult {
  const project = recalculatePalette(recognition.project);
  const hasCompleteLegendCounts = project.palette.every((entry) => entry.sourceCount != null);
  return {
    ...recognition,
    project,
    issues,
    totalFromCells: occupiedCount(project),
    totalFromLegend: hasCompleteLegendCounts
      ? project.palette.reduce((sum, entry) => sum + (entry.sourceCount ?? 0), 0)
      : null,
  };
}

function duplicateFor(projectPalette: PaletteEntry[], sourceId: number, selected: MardCatalogEntry): PaletteEntry | undefined {
  return projectPalette.find((entry) => {
    if (entry.id === sourceId) return false;
    const catalogCode = entry.catalogCode ?? findMardColor(entry.code)?.catalogCode;
    return catalogCode === selected.catalogCode;
  });
}

export function applyMardPaletteSelection(
  recognition: RecognitionResult,
  issueId: string | undefined,
  sourcePaletteId: number,
  selected: MardCatalogEntry,
  allowMerge = false,
): MardSelectionOutcome {
  const source = recognition.project.palette.find((entry) => entry.id === sourcePaletteId);
  if (!source) throw new Error("待修正的图例色号不存在");
  const duplicate = duplicateFor(recognition.project.palette, sourcePaletteId, selected);
  if (duplicate && !allowMerge) return { status: "requires-merge", duplicate };

  const project = cloneProject(recognition.project);
  const issues = updateIssues(recognition.issues, issueId, source.code, selected.displayCode);
  if (!duplicate) {
    project.palette = project.palette.map((entry) => entry.id === sourcePaletteId ? {
      ...entry,
      code: selected.displayCode,
      catalogCode: selected.catalogCode,
      detectedRGB: entry.detectedRGB ?? [...entry.displayRGB] as [number, number, number],
      displayRGB: [...selected.rgb] as [number, number, number],
      colorSource: "mard-catalog",
      catalogVersion: MARD_CATALOG_METADATA.catalogVersion,
      confidence: 100,
    } : entry);
    return { status: "applied", recognition: withRecalculatedTotals({ ...recognition, project }, issues), merged: false };
  }

  for (let index = 0; index < project.cells.length; index += 1) {
    if (project.cells[index] === sourcePaletteId) project.cells[index] = duplicate.id;
  }
  project.palette = project.palette
    .filter((entry) => entry.id !== sourcePaletteId)
    .map((entry) => entry.id === duplicate.id ? {
      ...entry,
      code: selected.displayCode,
      catalogCode: selected.catalogCode,
      detectedRGB: entry.detectedRGB ?? [...entry.displayRGB] as [number, number, number],
      displayRGB: [...selected.rgb] as [number, number, number],
      colorSource: "mard-catalog",
      catalogVersion: MARD_CATALOG_METADATA.catalogVersion,
      confidence: 100,
      sourceCount: entry.sourceCount != null && source.sourceCount != null ? entry.sourceCount + source.sourceCount : null,
    } : entry);
  if (project.selectedColorId === sourcePaletteId) project.selectedColorId = duplicate.id;
  if (project.currentMode === "color") {
    const selection = project.palette.findIndex((entry) => entry.id === project.selectedColorId);
    project.currentSelection = Math.max(0, selection);
  }
  return { status: "applied", recognition: withRecalculatedTotals({ ...recognition, project }, issues), merged: true };
}

export function applyCustomPaletteCode(
  recognition: RecognitionResult,
  issueId: string | undefined,
  sourcePaletteId: number,
  code: string,
): RecognitionResult {
  const normalizedCode = code.trim().toUpperCase();
  if (!/^[A-Z]{1,2}\d{1,2}$/.test(normalizedCode)) throw new Error("自定义色号格式不正确");
  if (findMardColor(normalizedCode)) throw new Error("该色号属于 MARD，请从完整色号库中选择");
  const source = recognition.project.palette.find((entry) => entry.id === sourcePaletteId);
  if (!source) throw new Error("待修正的图例色号不存在");
  if (recognition.project.palette.some((entry) => entry.id !== sourcePaletteId && entry.code === normalizedCode)) {
    throw new Error("该色号已经存在，请使用 MARD 选择器合并");
  }
  const project = cloneProject(recognition.project);
  project.palette = project.palette.map((entry) => entry.id === sourcePaletteId ? {
    ...entry,
    code: normalizedCode,
    catalogCode: undefined,
    detectedRGB: entry.detectedRGB ?? [...entry.displayRGB] as [number, number, number],
    colorSource: "manual",
    catalogVersion: undefined,
    confidence: 100,
  } : entry);
  return withRecalculatedTotals(
    { ...recognition, project },
    updateIssues(recognition.issues, issueId, source.code, normalizedCode),
  );
}
